// Tipos para el sistema de plazos administrativos

export type DeadlineCategory = 'hacienda' | 'contratacion' | 'urbanismo' | 'personal' | 'otros';

export type DeadlinePriority = 'alta' | 'media' | 'baja';

export type DeadlineStatus = 'pendiente' | 'en_curso' | 'completado' | 'vencido';

export type DeadlineDayType = 'habiles' | 'naturales' | 'fecha_fija';

export type HolidayScope = 'nacional' | 'andalucia' | 'extremadura' | 'local';

export type DeadlineSource = 'manual' | 'template' | 'document' | 'chat' | 'chronology';

export type NotificationChannel = 'app' | 'email' | 'telegram';

export type NotificationStatus = 'pending' | 'sent' | 'failed';

export interface Deadline {
  id: string;
  user_id: string;
  organization_id: string | null;
  chat_id: string | null;
  document_path: string | null;
  expediente_number: string | null;
  title: string;
  description: string | null;
  interested_party: string | null;
  due_date: string;
  original_due_date: string;
  category: DeadlineCategory;
  priority: DeadlinePriority;
  status: DeadlineStatus;
  day_type: DeadlineDayType;
  reminder_days: number[];
  is_critical: boolean;
  legal_reference: string | null;
  template_id: string | null;
  source_type: DeadlineSource;
  source_chronology_id?: string | null;
  source_event_id?: string | null;
  source_confidence?: number | null;
  created_at: string;
  updated_at: string;
}

export interface DeadlineTemplate {
  id: string;
  title: string;
  description: string | null;
  category: DeadlineCategory;
  day_type: DeadlineDayType;
  default_days: number | null;
  fixed_date: string | null;
  is_critical: boolean;
  legal_reference: string | null;
  scope: HolidayScope;
  is_active: boolean;
  created_at: string;
}

export interface Holiday {
  id: string;
  date: string;
  name: string;
  scope: HolidayScope;
  organization_id: string | null;
  year: number;
  created_at: string;
}

export interface DeadlineNotification {
  id: string;
  deadline_id: string;
  user_id: string;
  channel: NotificationChannel;
  scheduled_for: string;
  sent_at: string | null;
  status: NotificationStatus;
  error_message: string | null;
  created_at: string;
}

export interface CreateDeadlineInput {
  title: string;
  description?: string;
  expediente_number?: string;
  interested_party?: string;
  due_date: string;
  original_due_date?: string;
  category?: DeadlineCategory;
  priority?: DeadlinePriority;
  day_type?: DeadlineDayType;
  reminder_days?: number[];
  is_critical?: boolean;
  legal_reference?: string;
  template_id?: string;
  source_type?: DeadlineSource;
  source_chronology_id?: string | null;
  source_event_id?: string | null;
  source_confidence?: number | null;
  chat_id?: string;
  document_path?: string;
  organization_id?: string;
}

export interface UpdateDeadlineInput extends Partial<CreateDeadlineInput> {
  status?: DeadlineStatus;
}

// Para el parser de documentos
export interface ExtractedDocumentData {
  expedienteNumber: string | null;
  title: string | null;
  interestedParty: string | null;
  documentDate: Date | null;
  dueDate: Date | null;
  deadlineDays: number | null;
  deadlineType: 'habiles' | 'naturales' | null;
  category: DeadlineCategory | null;
  confidence: {
    expediente: number;
    dates: number;
    category: number;
    interested: number;
  };
}

// Colores para el calendario
export const DEADLINE_COLORS = {
  vencido: 'hsl(var(--destructive))',
  hoy: 'hsl(var(--destructive))',
  urgente: 'hsl(24, 95%, 53%)', // naranja
  proximo: 'hsl(48, 96%, 53%)', // amarillo
  normal: 'hsl(142, 76%, 36%)', // verde
  completado: 'hsl(var(--muted-foreground))',
} as const;

// Etiquetas en español
export const CATEGORY_LABELS: Record<DeadlineCategory, string> = {
  hacienda: 'Hacienda',
  contratacion: 'Contratación',
  urbanismo: 'Urbanismo',
  personal: 'Personal',
  otros: 'Otros',
};

export const PRIORITY_LABELS: Record<DeadlinePriority, string> = {
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

export const STATUS_LABELS: Record<DeadlineStatus, string> = {
  pendiente: 'Pendiente',
  en_curso: 'En curso',
  completado: 'Completado',
  vencido: 'Vencido',
};

export const DAY_TYPE_LABELS: Record<DeadlineDayType, string> = {
  habiles: 'Días hábiles',
  naturales: 'Días naturales',
  fecha_fija: 'Fecha fija',
};
